import PropTypes from 'prop-types';
import { useState } from 'react';
// shadcn
import { Avatar, AvatarImage, AvatarFallback } from 'src/@/components/ui/avatar';
import { ScrollArea } from 'src/@/components/ui/scroll-area';
// utils
import { fToNow } from '../../../utils/formatTime';
// components
import Image from '../../../components/Image';
import LightboxModal from '../../../components/LightboxModal';
import { FilePreviewComentarios } from '../../../components/upload';
// icons
import { MessageSquare } from 'lucide-react';

// ----------------------------------------------------------------------

const SLACK_COLORS = [
  'bg-[#E8912D] text-white',
  'bg-[#2BAC76] text-white',
  'bg-[#CD2553] text-white',
  'bg-[#4A154B] text-white',
  'bg-[#1264A3] text-white',
  'bg-[#E01E5A] text-white',
  'bg-[#36C5F0] text-white',
  'bg-[#ECB22E] text-white',
];

function getAvatarColor(name) {
  if (!name) return SLACK_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return SLACK_COLORS[Math.abs(hash) % SLACK_COLORS.length];
}

// ----------------------------------------------------------------------

KanbanTaskCommentList.propTypes = {
  comments: PropTypes.array,
  chamado: PropTypes.object,
};

export default function KanbanTaskCommentList({ comments, chamado }) {
  const [openLightbox, setOpenLightbox] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const arrayMimeTypesImages = [
    "image/jpeg",
    "image/png",
    "image/webp",
  ];
  const arrayMimeTypesArquivos = [
    "text/plain",
    "application/pdf"
  ]

  const handleOpenLightbox = (url) => {
    const selectedImage = url;
    setOpenLightbox(true);
    setSelectedImage(selectedImage);
  };

  function validaArquivo(comment) {
    if (comment.isMedia && comment.files.length > 0) {
      if (arrayMimeTypesArquivos.includes(comment.files[0].type)) {
        return (
          <FilePreviewComentarios files={comment.files} />
        )
      } else if (arrayMimeTypesImages.includes(comment.files[0].type)) {
        return (
          <div className="mt-1 rounded-lg overflow-hidden cursor-pointer border border-[#E0E0E0] dark:border-gray-700 max-w-[360px] hover:border-[#1264A3] dark:hover:border-[#1264A3] transition-colors">
            <Image
              src={`https://lais-app.s3.us-east-2.amazonaws.com/${comment.files[0].key}`}
              onClick={() => handleOpenLightbox(`https://lais-app.s3.us-east-2.amazonaws.com/${comment.files[0].key}`)}
              className="w-full"
            />
          </div>
        )
      }
    }
  }

  return (
    <>
      <div className="flex flex-col h-full bg-white dark:bg-[#1A1D21]">

        {/* Messages */}
        <div className="flex-1 overflow-hidden">
          {comments.length > 0 ? (
            <ScrollArea className="h-full">
              <div className="flex flex-col py-2">
                {comments.map((comment, index) => {
                  const prevComment = comments[index - 1];
                  const isSameAuthor = prevComment && prevComment.nome_autor === comment.nome_autor;
                  const prevTime = prevComment ? new Date(prevComment.data_criacao).getTime() : 0;
                  const currTime = new Date(comment.data_criacao).getTime();
                  const isGrouped = isSameAuthor && (currTime - prevTime) < 5 * 60 * 1000;

                  return (
                    <div
                      key={comment._id}
                      className={`group relative flex gap-2 px-5 hover:bg-[#F8F8F8] dark:hover:bg-[#222529] transition-colors ${isGrouped ? 'py-0.5' : 'py-2 mt-1'}`}
                    >
                      {/* Avatar ou espaço para timestamp no hover (agrupado) */}
                      <div className="w-9 flex-shrink-0 flex items-start pt-0.5">
                        {isGrouped ? (
                          <span className="hidden group-hover:flex w-full items-center justify-center text-[11px] text-[#616061] dark:text-[#ABABAD] font-normal">
                            {new Date(comment.data_criacao).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        ) : (
                          <Avatar className="w-9 h-9 rounded-lg">
                            <AvatarImage src={comment.avatar} alt={comment.nome_autor} className="rounded-lg" />
                            <AvatarFallback className={`rounded-lg font-bold text-sm ${getAvatarColor(comment.nome_autor)}`}>
                              {comment.nome_autor?.charAt(0)?.toUpperCase() || 'U'}
                            </AvatarFallback>
                          </Avatar>
                        )}
                      </div>

                      {/* Conteúdo da mensagem */}
                      <div className="flex-1 min-w-0">
                        {!isGrouped && (
                          <div className="flex items-baseline gap-2">
                            <span className="text-[15px] font-bold text-[#1D1C1D] dark:text-[#D1D2D3] hover:underline cursor-pointer">
                              {comment.nome_autor}
                            </span>
                            <span className="text-xs text-[#616061] dark:text-[#ABABAD] font-normal">
                              {fToNow(comment.data_criacao)}
                            </span>
                          </div>
                        )}

                        <div className="text-[15px] text-[#1D1C1D] dark:text-[#D1D2D3] leading-[1.46668]">
                          {comment.isMedia ? (
                            validaArquivo(comment)
                          ) : (
                            <span className="break-words whitespace-pre-wrap">{comment.texto}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          ) : (
            <div className="flex flex-col items-center justify-center h-full px-4">
              <div className="w-14 h-14 rounded-xl bg-[#F8F8F8] dark:bg-[#222529] flex items-center justify-center mb-3">
                <MessageSquare className="w-7 h-7 text-[#1264A3]" />
              </div>
              <p className="text-[15px] font-bold text-[#1D1C1D] dark:text-[#D1D2D3]">
                Nenhum comentário ainda
              </p>
              <p className="text-[13px] text-[#616061] dark:text-[#ABABAD] mt-1">
                Seja o primeiro a comentar neste chamado
              </p>
            </div>
          )}
        </div>
      </div>

      <LightboxModal
        images={[]}
        mainSrc={selectedImage}
        photoIndex={selectedImage}
        setPhotoIndex={setSelectedImage}
        isOpen={openLightbox}
        onCloseRequest={() => setOpenLightbox(false)}
      />
    </>
  );
}
